import { Box, makeStyles } from "@material-ui/core";
// MEMO: デフォルト月の自動入力をやめたため未使用（2026-01-02）
// import dayjs from "dayjs";
import * as React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useRecoilState } from "recoil";

import { CustomButton } from "@/components/Button";
import {
  RegistrationActions,
  RegistrationBack,
  RegistrationSection,
} from "@/components/property_registration/RegistrationActions";
import { CustomForm } from "@/components/property_registration/RegistrationInputs";
import { DatePickerComponent } from "@/components/property_registration/RegistrationInputs";
import { Toast } from "@/components/Toast";
import { PRICE_KEYS } from "@/constants/addNewProperty";
import theme from "@/theme";
import { AppEventTokenEnum, sendEventToAppMeasurementTool } from "@/utilities/capacitorLogic";
import { parseFormattedNumber } from "@/utilities/format";
import { getTaxRateAtBought } from "@/utilities/propertyManagement";
import { getRegistrationLoanErrors, RegistrationLoanField } from "@/validations/management/registrationLoan";
import { PropertyRegistrationState } from "@/view_models/atoms";

const useStyles = makeStyles(() => ({
  content: {
    width: 640,
    [theme.breakpoints.down("sm")]: {
      width: 342,
    },
    margin: "8px  auto 0px auto",
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": {
      borderBottom: "none",
    },
  },
}));

interface AddNewOwnBuildingPropertyLoanPageProps {
  onNext: () => void;
  onBack: () => void;
  validate: () => Promise<boolean>;
}

const AddNewOwnBuildingPropertyLoanPage: React.FC<AddNewOwnBuildingPropertyLoanPageProps> = ({
  onNext,
  onBack,
  validate,
}) => {
  const classes = useStyles();
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
    setError,
    clearErrors,
  } = useFormContext();
  const total_price = useWatch({
    name: PRICE_KEYS.total,
    control: control,
    defaultValue: 0,
  });

  const [formData] = useRecoilState(PropertyRegistrationState);

  // サーバーエラー
  const [serverError, setSeverError] = React.useState<string>("");

  const loanAmount = useWatch({ name: "loan_amount", control });

  const handleNext = async () => {
    clearErrors(["payment_starts_at", "loan_interest_rate_percentage"]);
    const loanErrors = getRegistrationLoanErrors(getValues());
    Object.entries(loanErrors).forEach(([field, message]) => {
      setError(field as RegistrationLoanField, { type: "manual", message });
    });
    if (Object.keys(loanErrors).length) return;
    const isValid = await validate();
    if (!isValid) return;

    // 追加の価格・ローン整合チェック（フォーム上に表示してブロック）
    clearErrors([
      "total_price_of_property",
      "loan_amount",
      "payment_period",
      "priceof_tax",
      "priceof_building",
      "priceof_land",
      "priceof_facilities",
    ]);
    const v = getValues();
    const total = parseFormattedNumber(v.total_price_of_property);
    const tax = parseFormattedNumber(v.priceof_tax);
    const building = parseFormattedNumber(v.priceof_building);
    const land = parseFormattedNumber(v.priceof_land);
    const facilities = parseFormattedNumber(v.priceof_facilities);
    const loanAmount = parseFormattedNumber(v.loan_amount);
    const paymentPeriod = parseFormattedNumber(v.payment_period);

    let hasError = false;
    // 購入価格は1以上の整数
    if (!(Number.isInteger(total) && total > 0)) {
      setError("total_price_of_property", { type: "manual", message: "1以上の整数を入力してください" });
      hasError = true;
    }
    // ローン額・返済年数は0以上の整数
    if (!Number.isInteger(loanAmount) || loanAmount < 0) {
      setError("loan_amount", { type: "manual", message: "0以上の整数を入力してください" });
      hasError = true;
    }
    if (!Number.isInteger(paymentPeriod) || paymentPeriod < 0) {
      setError("payment_period", { type: "manual", message: "0以上の整数を入力してください" });
      hasError = true;
    }
    // 価格内訳の負数チェック
    if ([tax, building, land, facilities].some((n) => n < 0)) {
      if (tax < 0) setError("priceof_tax", { type: "manual", message: "0以上の整数を入力してください" });
      if (building < 0) setError("priceof_building", { type: "manual", message: "0以上の整数を入力してください" });
      if (land < 0) setError("priceof_land", { type: "manual", message: "0以上の整数を入力してください" });
      if (facilities < 0) setError("priceof_facilities", { type: "manual", message: "0以上の整数を入力してください" });
      hasError = true;
    }
    const anyPartPositive = [tax, building, land, facilities].some((n) => n > 0);
    if (anyPartPositive) {
      const sum = tax + building + land + facilities;
      if (Math.abs(sum - total) > 5) {
        setError("total_price_of_property", {
          type: "manual",
          message: "購入価格が内訳合計と一致しません",
        });
        hasError = true;
      }
    }
    if (hasError) return;

    sendEventToAppMeasurementTool(AppEventTokenEnum.OwnPropertyPriceLoanCompleted);
    onNext();
  };

  const handleClose = React.useCallback(() => {
    setSeverError("");
  }, []);

  // 選択肢
  return (
    <>
      <Box>
        <Box className={classes.content} data-registration-content>
          <RegistrationSection title="購入価格" />

          <Controller
            name="total_price_of_property"
            control={control}
            defaultValue={formData.total_price_of_property || ""}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="購入価格 (税込)"
                required
                error={!!errors.total_price_of_property}
                errorText={errors.total_price_of_property?.message}
                placeHolder="100,000,000"
                unitSuffix="円"
                onChange={(e) => {
                  field.onChange(e);
                  const total = parseFormattedNumber(e.target.value);
                  // 建物(税込) = 購入価格の6割として自動計算
                  const building = total * 0.6;
                  // 土地 = 購入価格の4割として自動計算
                  const land = total * 0.4;

                  const tax_rate_at_bought = getTaxRateAtBought(formData.bought_at);
                  if (tax_rate_at_bought !== 0) {
                    // 建物(税抜)
                    const buildingWithOutTax = building / (1 + tax_rate_at_bought);
                    // うち消費税(土地は消費税かからないため、「うち消費税 = 建物消費税」となる)
                    const tax = building - buildingWithOutTax;

                    setValue(PRICE_KEYS.tax, Math.round(tax).toString());
                    setValue(PRICE_KEYS.building, Math.round(buildingWithOutTax).toString());
                    // うち建物付属設備は0円とする
                    setValue(PRICE_KEYS.facilities, "0");
                    setValue(PRICE_KEYS.land, Math.round(land).toString());
                  }
                }}
                formatNumeric
              />
            )}
          />

          <Controller
            name="priceof_tax"
            control={control}
            defaultValue={formData.priceof_tax || ""}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="うち消費税"
                required
                info="消費税は物件価格の割合として建物価格6割、土地価格4割と仮定して自動計算しておりますが、より詳細な値を出すには売買契約書記載の消費税額をご入力ください。"
                error={!!errors.priceof_tax}
                errorText={errors.priceof_tax?.message}
                placeHolder="100,000,000"
                unitSuffix="円"
                onChange={(e) => {
                  field.onChange(e);
                  // 前提
                  // 1. 自動計算の際は建物付属設備(税抜)を0にする
                  // 2. 建物合計 = 建物(税抜) + 建物付属設備(税抜) + 消費税
                  // 3. 購入価格(税込) = 建物合計 + 土地
                  const tax = parseFormattedNumber(e.target.value);

                  const tax_rate_at_bought = getTaxRateAtBought(formData.bought_at);
                  if (tax_rate_at_bought !== 0) {
                    // 建物(税抜)
                    const buildingWithOutTax = tax / tax_rate_at_bought;
                    // 土地
                    const land = parseFormattedNumber(String(total_price)) - tax - buildingWithOutTax;

                    setValue(PRICE_KEYS.building, Math.round(buildingWithOutTax).toString());
                    setValue(PRICE_KEYS.land, Math.round(land).toString());
                    setValue(PRICE_KEYS.facilities, "0");
                  }
                }}
                formatNumeric
              />
            )}
          />

          <Controller
            name="priceof_building"
            control={control}
            defaultValue={formData.priceof_building || ""}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="うち建物代"
                required
                info="本ツールでは消費税の金額を入力頂くと建物価格を自動計算して表示されますが、建物・建物付属設備の正確な価格内訳については物件を購入された際の担当者にご確認下さい。"
                error={!!errors.priceof_building}
                errorText={errors.priceof_building?.message}
                placeHolder="100,000,000"
                unitSuffix="円"
                onChange={(e) => {
                  field.onChange(e);
                  const values = getValues([PRICE_KEYS.total, PRICE_KEYS.tax, PRICE_KEYS.land]);
                  const result =
                    parseFormattedNumber(values.total_price_of_property) -
                    parseFormattedNumber(values.priceof_tax) -
                    parseFormattedNumber(e.target.value) -
                    parseFormattedNumber(values.priceof_land);
                  setValue(PRICE_KEYS.facilities, Math.round(result).toString());
                }}
                formatNumeric
              />
            )}
          />
          <Controller
            name="priceof_land"
            control={control}
            defaultValue={formData.priceof_land || ""}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="うち土地代"
                required
                info="本ツールでは購入価格・消費税の金額を入力頂くと土地価格を推定して表示します。"
                error={!!errors.priceof_land}
                errorText={errors.priceof_land?.message}
                placeHolder="100,000,000"
                unitSuffix="円"
                formatNumeric
              />
            )}
          />

          <RegistrationSection title="ローン" />

          <Controller
            name="loan_amount"
            control={control}
            defaultValue={formData.loan_amount || ""}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="ローン借入金額"
                required
                error={!!errors.loan_amount}
                errorText={errors.loan_amount?.message}
                placeHolder="100,000,000"
                unitSuffix="円"
                formatNumeric
              />
            )}
          />

          <Controller
            name="loan_interest_rate_percentage"
            control={control}
            defaultValue={formData.loan_interest_rate_percentage ?? ""}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="借入利率 (年利)"
                required={parseFormattedNumber(String(loanAmount ?? "0")) > 0}
                error={!!errors.loan_interest_rate_percentage}
                errorText={errors.loan_interest_rate_percentage?.message}
                placeHolder="10"
                unitSuffix="%"
                isShort
              />
            )}
          />
          <Controller
            name="payment_starts_at"
            control={control}
            // MEMO: 自動で当月を入れない（2026-01-02）
            // defaultValue={formData.payment_starts_at || dayjs(new Date()).format("YYYY-MM")}
            defaultValue={formData.payment_starts_at || null}
            render={({ ...field }) => (
              <DatePickerComponent
                label="初回返済月"
                required
                error={!!errors.payment_starts_at}
                errorText={errors.payment_starts_at?.message as string}
                {...field}
              />
            )}
          />

          <Controller
            name="payment_period"
            control={control}
            defaultValue={formData.payment_period || ""}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="返済年数"
                required
                error={!!errors.payment_period}
                errorText={errors.payment_period?.message}
                placeHolder="30"
                unitSuffix="年"
              />
            )}
          />
        </Box>
        <RegistrationActions>
          <CustomButton type="button" customVariant="filled" onClick={handleNext}>
            次へ
          </CustomButton>
          <RegistrationBack onClick={onBack} />
        </RegistrationActions>
      </Box>
      <Toast open={!!serverError} variant="error" message={serverError} onClose={handleClose} />
    </>
  );
};

export default AddNewOwnBuildingPropertyLoanPage;
